import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TrendingUp, Megaphone, Undo2, ArrowRight, ArrowUp, ArrowDown, ArrowUpDown, RefreshCw, FolderOpen, ChevronRight } from "lucide-react";
import { fmtDate } from "@/lib/utils";
import EditableCell from "@/components/EditableCell";
import { TableRowsSkeleton } from "@/components/LoadingSkeleton";

import GoogleUpdateBanner from "@/components/GoogleUpdateBanner";
import TrafficGrowthPanel from "@/components/seo/TrafficGrowthPanel";

/* SEO Performance — one row per Content project. Content Manager can edit
   DR / Keywords Indexed / Traffic inline. Rows meeting DR>=38 AND Traffic>=5000
   are surfaced in the purple "Ready to Market" banner up top with a per-project
   "Push to Marketing Team" action (or per-row "Pushed / Undo" state). */

const READY_DR = 38;
const READY_TRAFFIC = 5000;

// Formats a number with locale separators, guarding against NaN.
const numDisplay = (v) => Number(v ?? 0).toLocaleString();

// Iter 45.77 — green +N / red -N vs the previously saved traffic value.
const TrafficDiff = ({ row }) => {
  if (row.traffic_diff == null || row.traffic_diff === 0) return null;
  const up = row.traffic_diff > 0;
  return (
    <span
      className={`text-[11px] font-semibold tabular-nums px-1.5 py-[1px] rounded ${up ? "text-emerald-700 bg-emerald-50" : "text-red-600 bg-red-50"}`}
      title={`Previously ${numDisplay(row.traffic_prev)}`}
      data-testid={`seo-traffic-diff-${row.project_id}`}
    >
      {up ? "+" : "-"}{numDisplay(Math.abs(row.traffic_diff))}
    </span>
  );
};

export default function SEOPerformance() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // per-project busy flag
  const [expanded, setExpanded] = useState({}); // project_id -> growth panel open
  const toggleExpanded = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const canEdit = user && (
    user.role === "content_manager" || user.role === "admin" || user.role === "super_admin"
  );

  // Iter 45.28 — sortable numeric/date columns. Cycle: none → desc → asc →
  // none (desc first: metrics users want the biggest numbers on top).
  const [sortKey, setSortKey] = useState(null); // "dr" | "keywords_indexed" | "traffic" | "updated_at"
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("desc"); }
    else if (sortDir === "desc") setSortDir("asc");
    else { setSortKey(null); setSortDir("desc"); }
  };
  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (r) => sortKey === "updated_at"
      ? (r.updated_at ? new Date(r.updated_at).getTime() : 0)
      : Number(r[sortKey] ?? 0);
    return [...rows].sort((a, b) => (val(a) - val(b)) * dir);
  }, [rows, sortKey, sortDir]);

  // Header cell for a sortable column — highlighted when active, arrow
  // reflects direction, hover affordance on idle columns.
  const SortableTh = ({ label, k }) => (
    <th className="text-right px-5 py-3 sticky top-0 z-10 bg-slate-100">
      <button
        onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider font-semibold transition ${sortKey === k ? "text-purple-700" : "text-slate-500 hover:text-purple-600"}`}
        title={`Sort by ${label}`}
        data-testid={`seo-sort-${k}`}
        data-sort-state={sortKey === k ? sortDir : "off"}
      >
        {label}
        {sortKey === k && sortDir === "desc" && <ArrowDown className="h-3 w-3" />}
        {sortKey === k && sortDir === "asc" && <ArrowUp className="h-3 w-3" />}
        {sortKey !== k && <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </th>
  );

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/seo-performance");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const readyProjects = useMemo(
    () => rows.filter((r) => r.ready_to_market && !r.pushed_to_marketing),
    [rows]
  );

  const patchRow = (project_id, updates) => {
    setRows((all) => all.map((r) => (r.project_id === project_id ? { ...r, ...updates } : r)));
  };

  const saveMetric = (row, field) => async (n) => {
    const prev = { ...row };
    patchRow(row.project_id, { [field]: n });
    try {
      const { data } = await api.patch(`/seo-performance/${row.project_id}`, { [field]: n });
      patchRow(row.project_id, data);
    } catch (e) {
      patchRow(row.project_id, prev); // rollback
      toast.error(formatApiError(e));
    }
  };

  const pushOne = async (row) => {
    setBusy((b) => ({ ...b, [row.project_id]: true }));
    try {
      const { data } = await api.post(`/seo-performance/${row.project_id}/push-to-marketing`);
      patchRow(row.project_id, data);
      toast.success(`${row.project_name} pushed to Marketing`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, [row.project_id]: false })); }
  };

  const pushAll = async () => {
    if (readyProjects.length === 0) return;
    for (const r of readyProjects) { await pushOne(r); }
  };

  const undoPush = async (row) => {
    setBusy((b) => ({ ...b, [row.project_id]: true }));
    try {
      const { data } = await api.post(`/seo-performance/${row.project_id}/unpush-from-marketing`);
      patchRow(row.project_id, data);
      toast.success(`${row.project_name} removed from Marketing`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, [row.project_id]: false })); }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="seo-performance-page">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-8 py-5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white">
              <TrendingUp className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-slate-900">SEO Performance</h1>
              <div className="text-sm text-slate-500">
                Weekly metrics per project ·
                {" "}<span className="text-purple-700 font-medium">DR ≥ {READY_DR}</span>
                {" and "}<span className="text-purple-700 font-medium">Traffic ≥ {READY_TRAFFIC.toLocaleString()}</span>
                {" "}= ready to market
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={load} data-testid="seo-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="px-8 py-6 space-y-6">
          {/* Iter 45.68 — Google algorithm rollout banner (shared component,
              also mounted on the main Dashboard). Static per user feedback. */}
          <GoogleUpdateBanner />

          {/* Ready-to-market banner */}
          {readyProjects.length > 0 && (
            <div
              className="relative overflow-hidden rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 via-fuchsia-50 to-indigo-50 p-5"
              data-testid="ready-to-market-banner"
            >
              <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-purple-200/40 blur-3xl" />
              <div className="relative flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-purple-700 font-semibold">
                    <Megaphone className="h-5 w-5" />
                    These projects are ready to market
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    {readyProjects.length} project{readyProjects.length === 1 ? "" : "s"} passing the threshold. Push to the Marketing Team's "Ready to Market" sub-department.
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {readyProjects.slice(0, 8).map((r) => (
                      <span key={r.project_id} className="text-xs px-2 py-1 rounded-full bg-white/80 border border-purple-200 text-purple-800 font-medium">
                        {r.project_name} · DR {Math.round(r.dr)} · {r.traffic.toLocaleString()}
                      </span>
                    ))}
                    {readyProjects.length > 8 && (
                      <span className="text-xs px-2 py-1 rounded-full bg-white/80 border border-purple-200 text-purple-600">
                        +{readyProjects.length - 8} more
                      </span>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <Button
                    onClick={pushAll}
                    className="bg-purple-600 hover:bg-purple-700 text-white shrink-0"
                    data-testid="push-all-to-marketing-btn"
                  >
                    Push to Marketing Team <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Metrics table */}
          <Card className="bg-white border-slate-200 overflow-hidden" data-testid="seo-performance-table-card">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-800">Weekly performance</div>
              <div className="text-xs text-slate-500">
                {rows.length} project{rows.length === 1 ? "" : "s"}
                {!canEdit && <span className="ml-2 px-1.5 py-[1px] rounded bg-slate-100 text-slate-500">read-only</span>}
              </div>
            </div>
            {/* Iter 45.28 — the table wrapper is the vertical scroller
                (capped at 65vh) so the sticky <th>s stay pinned while the
                body scrolls. The previous `overflow-x-auto`-only wrapper
                broke sticky: clipping ancestors (Card overflow-hidden +
                x-scroll container) meant the header scrolled away. */}
            <div className="overflow-auto scrollbar-thin max-h-[65vh]">
              <table className="min-w-[900px] w-full text-sm">
                {/* Iter 45.28 — highlighted header row: solid slate-100 bg
                    (opaque so scrolling rows don't show through), stronger
                    text + bottom border, sticky so it stays fixed. */}
                <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider border-b-2 border-slate-200">
                  <tr>
                    <th className="text-left px-5 py-3 sticky top-0 z-10 bg-slate-100">Project</th>
                    <SortableTh label="DR" k="dr" />
                    <SortableTh label="Keyword Indexed" k="keywords_indexed" />
                    <SortableTh label="Traffic" k="traffic" />
                    <SortableTh label="Updated" k="updated_at" />
                    <th className="text-right px-5 py-3 sticky top-0 z-10 bg-slate-100">Ready to Market</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading && <TableRowsSkeleton cols={6} rows={5} firstAvatar />}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-400 italic">No projects yet</td></tr>
                  )}
                  {!loading && sortedRows.map((r) => {
                    const isReady = r.ready_to_market;
                    const isOpen = !!expanded[r.project_id];
                    return (
                      <React.Fragment key={r.project_id}>
                      <tr
                        className={`${isReady ? "bg-purple-50/40" : ""} ${isOpen ? "bg-slate-50" : ""} hover:bg-slate-50 transition-colors`}
                        data-testid={`seo-row-${r.project_id}`}
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <ChevronRight className={`h-4 w-4 text-slate-400 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-90 text-purple-600" : ""}`} />
                            <FolderOpen className="h-4 w-4 text-slate-400 shrink-0" />
                            <div className="min-w-0">
                              <button
                                onClick={() => toggleExpanded(r.project_id)}
                                className="font-medium text-slate-800 truncate block text-left hover:text-purple-700 transition-colors"
                                title={isOpen ? "Hide traffic growth" : "Show traffic growth"}
                                aria-expanded={isOpen}
                                data-testid={`seo-project-toggle-${r.project_id}`}
                              >
                                {r.project_name}
                              </button>
                              {r.site_url && (
                                <a
                                  href={r.site_url.startsWith("http") ? r.site_url : `https://${r.site_url}`}
                                  target="_blank" rel="noreferrer"
                                  className="text-xs text-slate-400 hover:text-purple-600 truncate block"
                                >
                                  {r.site_url}
                                </a>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <EditableCell
                            value={r.dr}
                            onSave={saveMetric(r, "dr")}
                            readOnly={!canEdit}
                            testId={`seo-dr-${r.project_id}`}
                            step={0.1} min={0} max={100}
                            formatDisplay={numDisplay}
                          />
                        </td>
                        <td className="px-5 py-3 text-right">
                          <EditableCell
                            value={r.keywords_indexed}
                            onSave={saveMetric(r, "keywords_indexed")}
                            readOnly={!canEdit}
                            testId={`seo-kw-${r.project_id}`}
                            step={1} min={0}
                            formatDisplay={numDisplay}
                          />
                        </td>
                        <td className="px-5 py-3 text-right">
                          <div className="inline-flex items-center justify-end gap-1.5">
                            <TrafficDiff row={r} />
                            <EditableCell
                              value={r.traffic}
                              onSave={saveMetric(r, "traffic")}
                              readOnly={!canEdit}
                              testId={`seo-traffic-${r.project_id}`}
                              step={1} min={0}
                              formatDisplay={numDisplay}
                            />
                          </div>
                        </td>
                        <td className="px-5 py-3 text-slate-500 text-xs">{fmtDate(r.updated_at)}</td>
                        <td className="px-5 py-3 text-right">
                          {r.pushed_to_marketing ? (
                            <div className="inline-flex items-center gap-2">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold" data-testid={`seo-pushed-badge-${r.project_id}`}>
                                Pushed
                              </span>
                              {canEdit && (
                                <button
                                  onClick={() => undoPush(r)}
                                  disabled={busy[r.project_id]}
                                  className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1 disabled:opacity-50"
                                  data-testid={`seo-undo-push-${r.project_id}`}
                                >
                                  <Undo2 className="h-3 w-3" /> Undo
                                </button>
                              )}
                            </div>
                          ) : canEdit ? (
                            // Push is always allowed on demand; threshold-met
                            // rows get the strong purple CTA, others get a
                            // subdued outline variant so intent stays clear.
                            <Button
                              size="sm"
                              onClick={() => pushOne(r)}
                              disabled={busy[r.project_id]}
                              className={
                                isReady
                                  ? "bg-purple-600 hover:bg-purple-700 text-white h-7 px-2.5 text-xs"
                                  : "bg-white border border-slate-200 text-slate-600 hover:border-purple-300 hover:text-purple-700 h-7 px-2.5 text-xs"
                              }
                              data-testid={`seo-push-btn-${r.project_id}`}
                              title={isReady ? "Meets threshold — recommended" : "Push on demand (below threshold)"}
                            >
                              Push <ArrowRight className="h-3 w-3 ml-1" />
                            </Button>
                          ) : isReady ? (
                            <span className="text-xs text-purple-700 font-semibold">Ready</span>
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr data-testid={`seo-growth-row-${r.project_id}`}>
                          <td colSpan={6} className="p-0">
                            <TrafficGrowthPanel projectId={r.project_id} refreshKey={r.traffic_updated_at} />
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
